import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, X, Play, ChevronRight } from "lucide-react";
import { API, apiService } from "../utils/api";

const SearchModal = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (query.length >= 2) {
        setLoading(true);
        try {
          const response = await axios.get(`${API}/search`, {
            params: { q: query, type: selectedType, limit: 20 }
          });
          setResults(response.data.results || []);
        } catch (error) {
          console.error('Search error:', error);
        }
        setLoading(false);
      } else {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query, selectedType]);

  const handleResultClick = (result) => {
    onClose();
    if (result.type === 'movie' || result.type === 'tvshow') {
      navigate('/library');
    } else if (result.type === 'channel') {
      navigate('/live-tv');
    } else if (result.type === 'ppv') {
      navigate('/');
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'movie': return '🎬';
      case 'tvshow': return '📺';
      case 'channel': return '📡';
      case 'ppv': return '⭐';
      default: return '🔍';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'movie': return 'Movie';
      case 'tvshow': return 'TV Show';
      case 'channel': return 'Channel';
      case 'ppv': return 'PPV';
      default: return type;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 p-4" data-testid="search-modal">
      <div className="max-w-3xl mx-auto">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X size={32} />
        </button>

        {/* Search Input */}
        <div className="mt-16 mb-6">
          <div className="relative">
            <Search size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search movies, TV shows, channels..."
              className="w-full bg-gray-900 border border-gray-700 rounded-xl pl-14 pr-4 py-4 text-white text-xl focus:border-red-600 focus:outline-none"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Type Filters */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setSelectedType(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !selectedType ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            All
          </button>
          {['movies', 'tvshows', 'channels', 'ppv'].map(type => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedType === type ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {type === 'movies' ? 'Movies' : type === 'tvshows' ? 'TV Shows' : type === 'channels' ? 'Channels' : 'PPV'}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Searching...</div>
          ) : query.length < 2 ? (
            <div className="text-center py-16">
              <Search size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Start typing to search</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-4">🔍</div>
              <p className="text-gray-400">No results found for "{query}"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full flex items-center gap-4 p-4 bg-gray-900 hover:bg-gray-800 rounded-lg text-left transition-colors"
                >
                  {result.thumbnail ? (
                    <img 
                      src={result.thumbnail} 
                      alt="" 
                      className="w-16 h-20 object-cover rounded"
                    />
                  ) : (
                    <div className="w-16 h-20 bg-gray-800 rounded flex items-center justify-center text-2xl">
                      {getTypeIcon(result.type)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300">
                        {getTypeLabel(result.type)}
                      </span>
                      {result.year && <span className="text-gray-500 text-sm">{result.year}</span>}
                      {result.genre && <span className="text-gray-500 text-sm">• {result.genre}</span>}
                    </div>
                    <h4 className="text-white font-medium truncate">{result.title}</h4>
                    {result.description && (
                      <p className="text-gray-500 text-sm line-clamp-1">{result.description}</p>
                    )}
                  </div>
                  <ChevronRight size={20} className="text-gray-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
